/**
 * The help wiki is generated from docs/USER_GUIDE.md — the same source as the
 * printable Word guide — so the two never drift. Each `## N.` chapter becomes a
 * chapter, each `###` sub-section an article, and `####` headings stay inside
 * their article as "On this page" anchors.
 */
import guideSource from '../../../../docs/USER_GUIDE.md?raw';

export type HelpGroup =
  'Getting started' | 'Members' | 'Mobile app' | 'Administrators' | 'Reference';

export const HELP_GROUPS: HelpGroup[] = [
  'Getting started',
  'Members',
  'Mobile app',
  'Administrators',
  'Reference',
];

const GROUP_BY_CHAPTER: Record<string, HelpGroup> = {
  '1': 'Getting started',
  '2': 'Getting started',
  '3': 'Getting started',
  '4': 'Getting started',
  '5': 'Members',
  '6': 'Mobile app',
  '7': 'Members',
  '8': 'Administrators',
  '9': 'Administrators',
  '10': 'Administrators',
};

export interface HelpHeading {
  id: string;
  text: string;
}

export interface HelpArticle {
  slug: string;
  /** "5.6", or null for un-numbered sub-sections and chapter overviews. */
  number: string | null;
  title: string;
  chapter: HelpChapter;
  body: string;
  headings: HelpHeading[];
  /** Lower-cased plain text, for search. */
  searchText: string;
  /** Plain text with original casing, for result snippets. */
  plainText: string;
}

export interface HelpChapter {
  slug: string;
  number: string;
  title: string;
  group: HelpGroup;
  articles: HelpArticle[];
}

/** Heading text → URL/anchor-safe id. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[`*]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** GitHub-style anchor, as used by links inside the markdown source. */
function githubAnchor(text: string): string {
  return text
    .toLowerCase()
    .replace(/[`*]/g, '')
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s/g, '-');
}

export function toPlainText(markdown: string): string {
  return markdown
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*\|?\s*:?-{3,}.*$/gm, '')
    .replace(/\|/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*`_]/g, '')
    .replace(/^\s*([-*]|\d+\.)\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseGuide(source: string) {
  const lines = source.split('\n');
  const chapters: HelpChapter[] = [];
  /** Markdown anchor → wiki path (`slug` or `slug#heading`). */
  const anchors = new Map<string, string>();
  /** Section number ("5.6", "8") → article slug. */
  const sections = new Map<string, string>();

  let chapter: HelpChapter | null = null;
  let article: { number: string | null; title: string; slug: string; lines: string[] } | null =
    null;

  const flushArticle = () => {
    if (!chapter || !article) return;
    const body = article.lines
      .join('\n')
      .replace(/^\s*---\s*$/gm, '')
      .trim();
    article.lines = [];
    // A chapter overview with no text of its own isn't worth a page.
    if (!body && article.slug === chapter.slug) {
      article = null;
      return;
    }
    const headings = [...body.matchAll(/^####\s+(.+)$/gm)].map((m) => {
      const text = m[1]!.trim();
      return { id: slugify(text), text: text.replace(/[`*]/g, '') };
    });
    const plainText = toPlainText(body);
    chapter.articles.push({
      slug: article.slug,
      number: article.number,
      title: article.title,
      chapter,
      body,
      headings,
      plainText,
      searchText: `${article.title} ${plainText}`.toLowerCase(),
    });
    for (const h of headings) {
      anchors.set(githubAnchor(h.text), `${article.slug}#${h.id}`);
    }
    article = null;
  };

  for (const line of lines) {
    const h2 = /^##\s+(\d+)\.\s+(.+)$/.exec(line);
    if (h2) {
      flushArticle();
      const number = h2[1]!;
      const title = h2[2]!.trim();
      const slug = `${number}-${slugify(title)}`;
      chapter = {
        slug,
        number,
        title,
        group: GROUP_BY_CHAPTER[number] ?? 'Reference',
        articles: [],
      };
      chapters.push(chapter);
      article = { number: null, title, slug, lines: [] };
      anchors.set(githubAnchor(`${number}. ${title}`), slug);
      sections.set(number, slug);
      continue;
    }
    if (/^##\s+/.test(line)) {
      // Un-numbered level-2 heading (the markdown's own Contents list): not wiki content.
      flushArticle();
      chapter = null;
      continue;
    }
    if (!chapter) continue;

    const h3 = /^###\s+(.+)$/.exec(line);
    if (h3) {
      flushArticle();
      const heading = h3[1]!.trim();
      const numbered = /^(\d+\.\d+)\s+(.+)$/.exec(heading);
      const number = numbered ? numbered[1]! : null;
      const title = (numbered ? numbered[2]! : heading).replace(/`/g, '');
      const slug = number
        ? `${number.replace('.', '-')}-${slugify(title)}`
        : `${chapter.number}-${slugify(title)}`;
      article = { number, title, slug, lines: [] };
      anchors.set(githubAnchor(heading), slug);
      if (number) sections.set(number, slug);
      continue;
    }
    article?.lines.push(line);
  }
  flushArticle();

  // A chapter whose overview was empty lands on its first article.
  for (const c of chapters) {
    const first = c.articles[0];
    if (first && first.slug !== c.slug) {
      anchors.set(githubAnchor(`${c.number}. ${c.title}`), first.slug);
      sections.set(c.number, first.slug);
    }
  }

  return { chapters, anchors, sections };
}

const parsed = parseGuide(guideSource);

export const HELP_CHAPTERS: HelpChapter[] = parsed.chapters;
export const HELP_ARTICLES: HelpArticle[] = parsed.chapters.flatMap((c) => c.articles);

const BY_SLUG = new Map(HELP_ARTICLES.map((a) => [a.slug, a]));

export function findArticle(slug: string): HelpArticle | undefined {
  return BY_SLUG.get(slug);
}

/** Resolve a `#anchor` link from the markdown source to a wiki path. */
export function resolveAnchor(anchor: string): string | undefined {
  const target = parsed.anchors.get(anchor);
  return target ? `/help/${target}` : undefined;
}

/** Resolve a "section 5.6" cross-reference to a wiki path. */
export function resolveSection(number: string): string | undefined {
  const slug = parsed.sections.get(number);
  return slug ? `/help/${slug}` : undefined;
}

export function articleDisplayTitle(a: HelpArticle): string {
  return a.number ? `${a.number} ${a.title}` : a.title;
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

export interface HelpSearchResult {
  article: HelpArticle;
  score: number;
  snippet: string;
}

export function searchTerms(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((t) => t.length >= 2);
}

function countOccurrences(haystack: string, needle: string): number {
  let count = 0;
  let from = 0;
  while (count < 20) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) break;
    count++;
    from = at + needle.length;
  }
  return count;
}

function snippetFor(article: HelpArticle, terms: string[]): string {
  const text = article.plainText;
  const lower = text.toLowerCase();
  const hits = terms.map((t) => lower.indexOf(t)).filter((i) => i >= 0);
  if (hits.length === 0) return text.slice(0, 180) + (text.length > 180 ? '…' : '');
  const at = Math.min(...hits);
  const start = Math.max(0, at - 70);
  const end = Math.min(text.length, at + 150);
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`;
}

/**
 * Every term must appear somewhere in the article. Title hits outweigh
 * sub-heading hits, which outweigh body mentions; an exact phrase gets a boost.
 */
export function searchHelp(query: string, limit = 30): HelpSearchResult[] {
  const terms = searchTerms(query);
  if (terms.length === 0) return [];
  const phrase = query.trim().toLowerCase();

  const results: HelpSearchResult[] = [];
  for (const article of HELP_ARTICLES) {
    if (
      !terms.every(
        (t) => article.searchText.includes(t) || article.chapter.title.toLowerCase().includes(t),
      )
    ) {
      continue;
    }
    const title = article.title.toLowerCase();
    const headings = article.headings.map((h) => h.text.toLowerCase()).join(' ');
    let score = 0;
    for (const t of terms) {
      if (title.includes(t)) score += 12;
      if (headings.includes(t)) score += 5;
      if (article.chapter.title.toLowerCase().includes(t)) score += 2;
      score += Math.min(countOccurrences(article.searchText, t), 6);
    }
    if (terms.length > 1) {
      if (title.includes(phrase)) score += 25;
      else if (article.searchText.includes(phrase)) score += 8;
    }
    results.push({ article, score, snippet: snippetFor(article, terms) });
  }
  return results.sort((a, b) => b.score - a.score).slice(0, limit);
}
